import { useCallback, useEffect, useRef, useState } from "react";

import { FORM_ERROR } from "final-form";

import {
  previewSearch,
  PreviewSearchErrors,
  SearchResponse,
} from "@/api/endpoints/search";
import { postTrackingData } from "@/api/endpoints/track";
import Episode from "@/containers/Episode";
import Episodes from "@/containers/Episodes";
import Layout from "@/containers/Layout";
import { useMessages } from "@/containers/Messages/MessageContext";
import { CapabilitiesProvider } from "@/contexts/CapabilitiesContext";
import { FEATURE } from "@/features";
import useSearchParams from "@/hooks/useSearchParams";
import { EVENT } from "@/tracking_events";
import { UnsavedSearch } from "@/types/Search";
import Search from "@/views/ArchiveSearch/Search";
import AllEarsTitle from "@components/AllEarsTitle";
import Container from "@components/Container";
import Pagination from "@components/Pagination";
import Rhythm from "@components/Rhythm";
import Typography from "@components/Typography";

import styles from "./ArchiveSearch.module.css";
import Header from "./Header";
import { Feature } from "@components/Feature";
import { Trans } from "@lingui/react/macro";
import Fallback from "./Fallback";
import { useParams } from "react-router-dom";

const PAGE_SIZE = 20;

const ArchiveSearch = () => {
  const [{ p = 1 }, setParams] = useSearchParams<{ p?: number }>();
  const { orgUuid } = useParams<{ orgUuid: string }>();
  const [result, setResult] = useState<
    (SearchResponse & PreviewSearchErrors) | null
  >(null);
  const { results, meta } = result || {};
  const [loading, setLoading] = useState<boolean>();
  const { danger } = useMessages();

  // Save the last made search to be able to paginate properly.
  const lastSearch = useRef<UnsavedSearch | null>(null);
  const resultsTarget = useRef<HTMLDivElement>(null);

  const search = useCallback(
    (values: UnsavedSearch, page?: number) => {
      setLoading(true);
      setResult(null);
      return previewSearch(values, {
        page_size: PAGE_SIZE,
        page_token: page ? (page - 1) * PAGE_SIZE : undefined,
      }).then((res) => {
        if (res.status === 200 && res.data) {
          lastSearch.current = values;
          setLoading(false);
          setResult(res.data);
          resultsTarget.current?.scrollIntoView({ behavior: "smooth" });
        } else if (res.status === 500) {
          danger({
            title: L.CUSTOMERS_SEARCH_500_ERROR_TITLE,
            message: L.CUSTOMERS_SEARCH_500_ERROR_MESSAGE,
          });
        } else if (res.status === 400 && res.data) {
          // Data is a list of errors. Merge and circle back to the form.
          return { [FORM_ERROR]: (res.data as string[]).join("\n") };
        } else {
          // All other errors should be field-level, final-form handles this
          return res;
        }
      });
    },
    [danger]
  );

  const handleSearch = useCallback(
    (values: UnsavedSearch) => {
      // Reset page count on search
      lastSearch.current = null;
      setParams({ p: undefined });
      postTrackingData(EVENT.DID_SEARCH, values);
      return search(values, undefined);
    },
    [setParams, search]
  );

  // Handles pagination
  useEffect(() => {
    if (lastSearch.current) {
      search(lastSearch.current, p);
    }
  }, [p, search]);

  return (
    <Layout>
      <CapabilitiesProvider>
        <AllEarsTitle title={L.EXPLORE_TITLE} />

        <Header />

        <Feature
          required={FEATURE.HAS_SEARCH_PRODUCT}
          fallback={<Fallback orgUuid={orgUuid} />}
        >
          <>
            <div className="border-b">
              <div className="w-full p-4 lg:p-6 max-w-7xl flex flex-col gap-1">
                <h1 className="text-2xl font-bold">
                  <Trans>Sök</Trans>
                </h1>
                <p className="text-sm text-muted-foreground">
                  <Trans>Sök i allt som All Ears har hört.</Trans>
                </p>
              </div>
            </div>

            <Container>
              <Rhythm>
                <Search
                  onSearch={handleSearch}
                  hasResults={Boolean(results && results.length > 0)}
                />
              </Rhythm>
              <Rhythm>
                <span ref={resultsTarget} />
                <Typography variant="h2">
                  {meta
                    ? (meta?.total_matches || 0) > 0
                      ? L.EXPLORE_RESULTS_COUNT.replace(
                          "{}",
                          meta?.total_matches
                        )
                      : L.EXPLORE_NO_RESULTS
                    : null}
                </Typography>
              </Rhythm>
              <Rhythm>
                <Episodes>
                  {loading ? (
                    <>
                      <Episode key={1} className="" loading />
                      <Episode key={2} className="" loading />
                      <Episode key={3} className="" loading />
                    </>
                  ) : (
                    results?.map((r) => (
                      <Episode
                        className=""
                        key={r.episode_uuid}
                        {...r}
                        {...r.post_data}
                        hideActions
                        hideTags
                      />
                    ))
                  )}
                </Episodes>
                {(meta?.total_matches || 0) > 0 && (
                  <Pagination
                    className={styles.pagination}
                    previous={() => {
                      setParams({ p: p - 1 });
                    }}
                    next={() => {
                      setParams({ p: p + 1 });
                    }}
                    disabled={false}
                    current={p}
                    max={Math.round((meta?.total_matches || 1) / PAGE_SIZE)}
                  />
                )}
              </Rhythm>
              <Rhythm>
                <div className={styles.disclaimers}>
                  <small className={styles.youtube}>
                    {L.EXPLORE_DISCLAIMER}
                  </small>
                </div>
              </Rhythm>
            </Container>
          </>
        </Feature>
      </CapabilitiesProvider>
    </Layout>
  );
};
export default ArchiveSearch;
